import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  useToaster,
  Pagination
} from 'rsuite';
import GearIcon from '@rsuite/icons/Gear';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList } from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import { fetchUntracedUsers } from '../../../services/untracedUsers.service';
import { getAdminUsers } from '../../../services/user.service';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import ConfigurationDrawer from './ConfigurationDrawer';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const { data, loading } = useSelector(state => state.untracedUsers);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [showDrawer, setShowDrawer] = useState(false);
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    userId: '',
    gpn: '',
    sl: ''
  });
  const [showConfigDrawer, setShowConfigDrawer] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  });

  useEffect(() => {
    dispatch(getAdminUsers());
  }, [dispatch]);

  const applyFilters = (dataToFilter, searchTerm, currentFilters) => {
    let filtered = [...dataToFilter];

    if (searchTerm.trim()) {
      const searchStr = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        item =>
          `${item.firstname || ''} ${item.lastname || ''}`.toLowerCase().includes(searchStr) ||
          (item.gpn || '').toLowerCase().includes(searchStr) ||
          (item.grade || '').toLowerCase().includes(searchStr) ||
          (item.sl || '').toLowerCase().includes(searchStr)
      );
    }

    if (currentFilters.userId) {
      const selectedUser = users.find(user => user._id === currentFilters.userId);
      if (selectedUser) {
        filtered = filtered.filter(item => {
          const itemFullName = `${item.firstname || ''} ${item.lastname || ''}`.toLowerCase();
          const userFullName = `${selectedUser.firstname || ''} ${
            selectedUser.lastname || ''
          }`.toLowerCase();
          return itemFullName === userFullName;
        });
      }
    }

    if (currentFilters.gpn) {
      filtered = filtered.filter(item =>
        (item.gpn || '').toLowerCase().includes(currentFilters.gpn.toLowerCase())
      );
    }

    if (currentFilters.sl) {
      filtered = filtered.filter(item => item.sl === currentFilters.sl);
    }

    return filtered;
  };

  useEffect(() => {
    const newFilteredData = applyFilters(data, searchKeyword, filters);
    console.log('Filtered data length:', newFilteredData.length);
    setFilteredData(newFilteredData);
    setPage(1); // Reset to page 1 when filters change
  }, [data, searchKeyword, filters]);

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);

      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      startDate &&
        endDate &&
        dispatch(
          fetchUntracedUsers(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'))
        );
    }
  }, [dateRange, dispatch]);

  const handleChangeLimit = dataKey => {
    console.log('Changing limit to:', dataKey);
    setPage(1);
    setLimit(dataKey);
  };

  const handleSearch = searchTerm => {
    console.log('Search term:', searchTerm);
    setSearchKeyword(searchTerm);
    setPage(1);
  };

  const handleFiltersChange = newFilters => {
    console.log('Filters changed:', newFilters);
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    console.log('Resetting filters');
    setFilters({
      userId: '',
      gpn: '',
      sl: ''
    });
    setPage(1);
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const untracedUsersToExport = filteredData.map(item => {
      return {
        'Full Name': `${item?.firstname || ''} ${item?.lastname || ''}`,
        'Registration Number': item?.gpn || '',
        Grade: item.grade || '',
        Team: item.sl || '',
        'Created At': new Date(item._id.createdAt).toLocaleString()
      };
    });

    downloadExcel(untracedUsersToExport, 'Untraced-Users');
  };

  // Calculate paginated data
  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);
  console.log('Paginated data:', {
    page,
    limit,
    total: filteredData.length,
    displayed: paginatedData.length,
    data: paginatedData
  });

  const { t } = useTranslation();

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder="Date Range"
            editable={false}
            ranges={[
              {
                label: t('Last 30 Days'),
                value: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()]
              },
              {
                label: t('Last 7 Days'),
                value: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]
              },
              {
                label: t('Today'),
                value: [new Date(new Date().setDate(new Date().getDate())), new Date()]
              }
            ]}
          />
        </Stack>

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdFilterList} style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
          <Button appearance="primary" onClick={() => setShowConfigDrawer(true)}>
            <GearIcon style={{ marginRight: 8 }} /> {t('Configuration')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 400)}
          data={paginatedData}
          loading={loading}
        >
          <Column flexGrow={4}>
            <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
            <Cell>{rowData => `${rowData.firstname || ''} ${rowData.lastname || ''}`}</Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Registration Number')}</HeaderCell>
            <Cell dataKey="gpn" />
          </Column>

          <Column flexGrow={2}>
            <HeaderCell>{t('Grade')}</HeaderCell>
            <Cell dataKey="grade" />
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Team')}</HeaderCell>
            <Cell dataKey="sl" />
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Date')}</HeaderCell>
            <Cell>
              {rowData =>
                `${new Date(rowData.date).toLocaleDateString()} ${rowData.timeslot || ''}`
              }
            </Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={filteredData.length}
            limitOptions={[10, 20, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={users}
        usersLoading={usersLoading}
        toaster={toaster}
      />
      <ConfigurationDrawer open={showConfigDrawer} onClose={() => setShowConfigDrawer(false)} />
    </>
  );
};

export default DataTable;
